import { useEffect, useState } from 'react';
import { apiFetch } from '../api/http';
import { ca } from '../i18n/ca';

type Side = 'A' | 'B';

interface Pair {
  id: string;
  words: string;
  chosen: Side | null;
}

interface Tally {
  comparison: string;
  left: string;
  leftVotes: number;
  right: string;
  rightVotes: number;
  listeners: number;
}

/**
 * The blind pair page (VOICE_QUALITY_STUDY.md §7), at /parells-de-veu, an
 * admin's: the same line said two ways, one choice per pair, and which way is
 * which only in the count, asked for when the listening is over. The page is
 * reached by its address: it lives as long as the question.
 */
export function VoicePairs() {
  const v = ca.voicePairs;
  const [pairs, setPairs] = useState<Pair[] | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [failed, setFailed] = useState(false);
  const [tally, setTally] = useState<Tally[] | null>(null);

  useEffect(() => {
    apiFetch<{ enabled: boolean; pairs: Pair[] }>('/api/admin/voice-pairs')
      .then((page) => {
        setEnabled(page.enabled);
        setPairs(page.pairs);
      })
      .catch(() => setFailed(true));
  }, []);

  const choose = (id: string, side: Side) => {
    setFailed(false);
    apiFetch(`/api/admin/voice-pairs/${id}/choice`, { method: 'POST', body: { side } })
      .then(() =>
        setPairs((all) => (all ?? []).map((p) => (p.id === id ? { ...p, chosen: side } : p))),
      )
      .catch(() => setFailed(true));
  };

  const showTally = () => {
    apiFetch<Tally[]>('/api/admin/voice-pairs/tally')
      .then(setTally)
      .catch(() => setFailed(true));
  };

  const done = pairs?.filter((p) => p.chosen).length ?? 0;
  return (
    <main className="page">
      <h1>{v.title}</h1>
      <p className="muted">{v.intro}</p>
      {failed && <p role="alert">{v.failed}</p>}
      {!enabled && <p role="status">{v.voiceOff}</p>}
      {pairs && (
        <>
          <p>{v.progress(done, pairs.length)}</p>
          <ol>
            {pairs.map((p) => (
              <li key={p.id}>
                <p>{p.words}</p>
                {(['A', 'B'] as const).map((side) => (
                  <div key={side}>
                    <audio
                      controls
                      preload="none"
                      aria-label={v.listen(side)}
                      src={`/api/admin/voice-pairs/${p.id}/audio/${side}`}
                    />
                    <label>
                      <input
                        type="radio"
                        name={p.id}
                        checked={p.chosen === side}
                        onChange={() => choose(p.id, side)}
                      />{' '}
                      {v.prefer(side)}
                    </label>
                  </div>
                ))}
              </li>
            ))}
          </ol>
          <button type="button" className="btn" onClick={showTally}>
            {v.showTally}
          </button>
        </>
      )}
      {tally && (
        <ul aria-label={v.tallyTitle}>
          {tally.map((t) => (
            <li key={t.comparison}>
              {v.tallyRow(t.left, t.leftVotes, t.right, t.rightVotes, t.listeners)}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
